'use client'

import { useEffect, useState, useRef, VideoHTMLAttributes } from 'react'
import { useMeasure } from 'react-use'
import { VideoPlayerControls } from '@/components/video-player-controls'
import { cn } from '@/utils/styles/cn'

interface VideoPlayerProps extends VideoHTMLAttributes<HTMLVideoElement> {
    /**
     * Replaces the video surface's click behavior (single-click play/pause, double-click expand)
     * — e.g. a card that opens its preview modal instead. Playback and expand stay reachable
     * through the control bar.
     */
    onSurfaceClick?: () => void
    poster?: string
    onExpandChange?: (expanded: boolean) => void
    muted?: boolean
    /**
     * Host-driven play/pause (e.g. ResourceCard `videoPlayMode="hover"`). When set, the host owns
     * play state; leaving hover pauses and resets to the poster. Leave undefined for autoPlay /
     * control-bar self-management.
     */
    playing?: boolean
}

export function VideoPlayer({
    className,
    poster,
    onExpandChange,
    onSurfaceClick,
    muted,
    playing,
    ...props
}: VideoPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null)
    const [containerRef, { width: containerWidth, height: containerHeight }] = useMeasure<HTMLDivElement>()
    const [intrinsic, setIntrinsic] = useState<{ width: number; height: number } | null>(null)
    const [isPlaying, setIsPlaying] = useState(props.autoPlay ?? playing ?? false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [volume, setVolume] = useState(1)
    const [isMuted, setIsMuted] = useState(muted ?? false)
    const [isExpanded, setIsExpanded] = useState(false)
    const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null)

    useEffect(() => {
        setIsMuted(muted ?? false)
    }, [muted])

    useEffect(() => {
        if (playing === undefined) return
        const video = videoRef.current
        if (!video) return
        if (playing) {
            void video.play()?.catch((error) => {
                console.error('Error playing video:', error)
            })
            setIsPlaying(true)
        } else {
            video.pause()
            video.currentTime = 0
            setIsPlaying(false)
            setCurrentTime(0)
        }
    }, [playing])

    useEffect(() => {
        if (!videoRef.current) return
        videoRef.current.volume = isMuted ? 0 : volume
    }, [isMuted, volume])

    useEffect(() => {
        const video = videoRef.current
        if (!video) return

        const updateTime = () => setCurrentTime(video.currentTime)
        // Duration and intrinsic size land on the same event, and the fit below needs both.
        const updateMetadata = () => {
            setDuration(video.duration)
            if (video.videoWidth > 0 && video.videoHeight > 0) {
                setIntrinsic({ width: video.videoWidth, height: video.videoHeight })
            }
        }
        const handleEnd = () => setIsPlaying(false)

        video.addEventListener('timeupdate', updateTime)
        video.addEventListener('loadedmetadata', updateMetadata)
        video.addEventListener('ended', handleEnd)

        return () => {
            video.removeEventListener('timeupdate', updateTime)
            video.removeEventListener('loadedmetadata', updateMetadata)
            video.removeEventListener('ended', handleEnd)
        }
    }, [])

    /**
     * The rect `object-contain` actually paints: `scale = min(containerW/videoW, containerH/videoH)`,
     * centred. Measured rather than expressed in CSS because a contain fit needs BOTH container axes
     * in one expression, and `aspect-ratio` + `max-width`/`max-height` cannot: whichever axis is
     * definite wins, and the max on the other clips the ratio instead of shrinking the box back
     * through it.
     *
     * ONLY the control bar consumes this, and its frame is absolutely positioned — so nothing here is
     * in the layout flow. That is load-bearing, not tidiness: an in-flow box sized from a measurement
     * of its own container is self-referential wherever that container's height is content-driven
     * (`scale(n+1) = min(cw/vw, scale(n))` is monotone non-increasing), so it latches on the smallest
     * value ever measured — the pre-metadata one, i.e. the poster's ratio or the HTML default 300x150
     * — and can never grow back. Keeping the video in flow and untouched leaves every mount's layout
     * byte-identical to before this fix, and leaves the measurement a pure function of a box the bar
     * cannot influence.
     */
    const paintedRect =
        intrinsic && containerWidth > 0 && containerHeight > 0
            ? (() => {
                  const scale = Math.min(containerWidth / intrinsic.width, containerHeight / intrinsic.height)
                  const width = intrinsic.width * scale
                  const height = intrinsic.height * scale
                  return { left: (containerWidth - width) / 2, top: (containerHeight - height) / 2, width, height }
              })()
            : null

    const togglePlay = () => {
        if (!videoRef.current) return
        if (isPlaying) {
            videoRef.current.pause()
        } else {
            videoRef.current.play()?.catch((error) => {
                console.error('Error playing video:', error)
            })
        }
        setIsPlaying(!isPlaying)
    }

    const handleTimeChange = (seconds: number) => {
        if (!videoRef.current) return
        videoRef.current.currentTime = seconds
        setCurrentTime(seconds)
    }

    const handleVolumeChange = (newVolume: number) => {
        if (!videoRef.current) return
        videoRef.current.volume = newVolume
        setVolume(newVolume)
        setIsMuted(newVolume === 0)
    }

    const toggleMute = () => {
        if (!videoRef.current) return
        const newMuted = !isMuted
        videoRef.current.volume = newMuted ? 0 : volume
        setIsMuted(newMuted)
    }

    const toggleExpand = () => {
        const newExpanded = !isExpanded
        setIsExpanded(newExpanded)
        onExpandChange?.(newExpanded)
    }

    const handleVideoClick = () => {
        if (onSurfaceClick) {
            onSurfaceClick()
            return
        }
        if (clickTimeout) {
            // Double click detected
            clearTimeout(clickTimeout)
            setClickTimeout(null)
            toggleExpand()
        } else {
            // Single click handling with delay to detect double clicks
            setClickTimeout(
                setTimeout(() => {
                    togglePlay()
                    setClickTimeout(null)
                }, 250)
            )
        }
    }

    return (
        <div ref={containerRef} className="relative group h-full w-full">
            <div className="absolute inset-0 z-10 cursor-pointer" onClick={handleVideoClick} />
            <video
                ref={videoRef}
                className={cn('w-full h-full object-contain', className)}
                poster={poster}
                muted={isMuted}
                {...props}
            />
            {/* The bar's frame — the painted picture's rect, so the bar spans the VIDEO rather than the
                element. `object-contain` letterboxes the picture inside a box whose aspect the CONTAINER
                dictates, and a bar anchored to that box overhangs by exactly the letterbox: the reported
                bug. Positioning the frame (rather than resizing the video) keeps the fix entirely out of
                the layout — see `paintedRect`.

                Before metadata the aspect is unknown, so it falls back to the full element: today's
                behaviour exactly. Nothing jumps when the real rect lands — `object-contain` was already
                painting the picture there, so the bar snaps onto the picture and the picture does not move.

                `pointer-events-none` so the letterbox keeps passing clicks to the play catcher beneath;
                the bar itself opts back in. */}
            <div className="absolute z-20 pointer-events-none" style={paintedRect ?? { inset: 0 }}>
                <VideoPlayerControls
                    className="pointer-events-auto"
                    isPlaying={isPlaying}
                    currentTime={currentTime}
                    duration={duration}
                    volume={volume}
                    isMuted={isMuted}
                    onTogglePlay={togglePlay}
                    onSeek={handleTimeChange}
                    onVolumeChange={handleVolumeChange}
                    onToggleMute={toggleMute}
                    isExpanded={isExpanded}
                    onToggleExpand={toggleExpand}
                />
            </div>
        </div>
    )
}
